import api from './apiClient';

export interface AttentionCall {
  _id: string;
  code: string;
  company: { _id: string; name: string } | string;
  description: string;
  occurredAt: string;
  reportedBy: { _id: string; firstName: string; lastName: string; email: string } | string;
  targetUsers: Array<{
    // Opcional: la persona señalada puede no tener cuenta en la plataforma
    // (contratista, personal de paso). Entonces sólo viaja `name`.
    user?: { _id: string; firstName: string; lastName: string; email: string } | string;
    name: string;
    role?: string;
  }>;
  severity: 'leve' | 'moderado' | 'grave';
  attachments: string[];
  status: 'open' | 'reviewed' | 'closed';
  reviewedBy?: { _id: string; firstName: string; lastName: string; email: string } | string;
  reviewNotes?: string;
  fine?: {
    amount: number;
    currency: string;
    status: 'proposed' | 'approved' | 'rejected';
    approvedBy?: { _id: string; firstName: string; lastName: string; email: string } | string;
    approvedAt?: string;
    rejectionReason?: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface CreateAttentionCallData {
  description: string;
  occurredAt: string;
  companyId?: string;
  targetUsers?: Array<{ user?: string; name: string; role?: string }>;
  severity?: 'leve' | 'moderado' | 'grave';
  fineAmount?: number;
  fineCurrency?: string;
  attachments?: File[];
}

interface UpdateAttentionCallData {
  description?: string;
  occurredAt?: string;
  targetUsers?: Array<{ user?: string; name: string; role?: string }>;
  severity?: 'leve' | 'moderado' | 'grave';
  fineAmount?: number;
  fineCurrency?: string;
  keepAttachments?: string[];
  attachments?: File[];
}

class AttentionCallApi {
  async create(data: CreateAttentionCallData): Promise<AttentionCall> {
    const formData = new FormData();
    formData.append('description', data.description);
    formData.append('occurredAt', data.occurredAt);

    if (data.companyId) {
      formData.append('companyId', data.companyId);
    }

    if (data.targetUsers) {
      formData.append('targetUsers', JSON.stringify(data.targetUsers));
    }

    if (data.severity) {
      formData.append('severity', data.severity);
    }

    if (data.fineAmount != null && data.fineAmount > 0) {
      formData.append('fineAmount', String(data.fineAmount));
      if (data.fineCurrency) {
        formData.append('fineCurrency', data.fineCurrency);
      }
    }

    if (data.attachments) {
      for (const file of data.attachments) {
        formData.append('attachments', file);
      }
    }

    const result = await api.post('/attention-calls', formData);
    return result.data;
  }

  async getAll(params?: {
    page?: number;
    limit?: number;
    status?: string;
    severity?: string;
    company?: string;
  }): Promise<{ data: AttentionCall[]; pagination: any }> {
    const queryParams: Record<string, any> = {};
    if (params?.page) queryParams.page = String(params.page);
    if (params?.limit) queryParams.limit = String(params.limit);
    if (params?.status) queryParams.status = params.status;
    if (params?.severity) queryParams.severity = params.severity;
    if (params?.company) queryParams.company = params.company;

    return await api.get('/attention-calls', queryParams);
  }

  async getById(id: string): Promise<AttentionCall> {
    const result = await api.get(`/attention-calls/${id}`);
    return result.data;
  }

  async update(id: string, data: UpdateAttentionCallData): Promise<AttentionCall> {
    const formData = new FormData();
    if (data.description !== undefined) formData.append('description', data.description);
    if (data.occurredAt !== undefined) formData.append('occurredAt', data.occurredAt);
    if (data.targetUsers !== undefined) formData.append('targetUsers', JSON.stringify(data.targetUsers));
    if (data.severity !== undefined) formData.append('severity', data.severity);
    if (data.fineAmount !== undefined) formData.append('fineAmount', String(data.fineAmount));
    if (data.fineCurrency !== undefined) formData.append('fineCurrency', data.fineCurrency);
    if (data.keepAttachments !== undefined) formData.append('keepAttachments', JSON.stringify(data.keepAttachments));
    if (data.attachments) {
      for (const file of data.attachments) {
        formData.append('attachments', file);
      }
    }

    const result = await api.put(`/attention-calls/${id}`, formData);
    return result.data;
  }

  async getAttachmentUrl(id: string, index: number): Promise<{ url: string; originalName?: string; contentType?: string }> {
    const result = await api.get(`/attention-calls/${id}/attachments/${index}`);
    return result.data;
  }

  async updateStatus(
    id: string,
    status: string,
    options?: {
      reviewNotes?: string;
      fineAction?: 'approve' | 'reject';
      fineRejectionReason?: string;
    },
  ): Promise<AttentionCall> {
    const body: any = { status };
    if (options?.reviewNotes !== undefined) body.reviewNotes = options.reviewNotes;
    if (options?.fineAction) body.fineAction = options.fineAction;
    if (options?.fineRejectionReason) body.fineRejectionReason = options.fineRejectionReason;
    const result = await api.patch(`/attention-calls/${id}/status`, body);
    return result.data;
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/attention-calls/${id}`);
  }

  async getStats(params?: { company?: string }): Promise<{ open: number; reviewed: number; closed: number }> {
    const queryParams: Record<string, any> = {};
    if (params?.company) queryParams.company = params.company;
    const result = await api.get('/attention-calls/stats', queryParams);
    return result.data;
  }

  async exportPdf(params?: { company?: string }): Promise<Blob> {
    const queryParams: Record<string, any> = {};
    if (params?.company) queryParams.company = params.company;

    return api.getBlob('/exports/attention-calls', queryParams);
  }
}

export const attentionCallApi = new AttentionCallApi();
